import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { workoutService } from '../services';
import { Check, SkipForward, X } from 'lucide-react';

const Workout = () => {
  const { logId } = useParams();
  const navigate = useNavigate();
  const [workout, setWorkout] = useState(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSeries, setCurrentSeries] = useState(1);
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [isResting, setIsResting] = useState(false);
  const [restTime, setRestTime] = useState(0);
  const [startTime] = useState(Date.now());
  const [seriesData, setSeriesData] = useState({
    actual_reps: '',
    actual_weight: '',
    intensity: 5,
  });

  useEffect(() => {
    loadWorkout();
  }, [logId]);

  useEffect(() => {
    let interval;
    if (isResting && restTime > 0) {
      interval = setInterval(() => {
        setRestTime((prev) => {
          if (prev <= 1) {
            setIsResting(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isResting, restTime]);

  const loadWorkout = async () => {
    try {
      const response = await workoutService.get(logId);
      setWorkout(response.data);
      
      if (response.data.routine?.routine_exercises?.length > 0) {
        const firstEx = response.data.routine.routine_exercises[0];
        setSeriesData({
          actual_reps: firstEx.target_reps,
          actual_weight: firstEx.target_weight || '',
          intensity: 5,
        });
      }
    } catch (error) {
      console.error('Error loading workout:', error);
    }
  };

  const handleFinishSeries = () => {
    setShowSeriesModal(true);
  };

  const handleSaveSeriesData = async () => {
    try {
      const currentExercise = workout.routine.routine_exercises[currentExerciseIndex];
      
      await workoutService.addSeries(logId, {
        exercise_id: currentExercise.exercise_id,
        series_number: currentSeries,
        actual_reps: parseInt(seriesData.actual_reps),
        actual_weight: parseFloat(seriesData.actual_weight),
        intensity: parseInt(seriesData.intensity),
        rest_duration: 60,
      });

      setShowSeriesModal(false);
      
      // Check if we need to move to next series or exercise
      if (currentSeries < currentExercise.target_series) {
        setCurrentSeries(currentSeries + 1);
        setIsResting(true);
        setRestTime(60);
      } else if (currentExerciseIndex < workout.routine.routine_exercises.length - 1) {
        setCurrentExerciseIndex(currentExerciseIndex + 1);
        setCurrentSeries(1);
        const nextEx = workout.routine.routine_exercises[currentExerciseIndex + 1];
        setSeriesData({
          actual_reps: nextEx.target_reps,
          actual_weight: nextEx.target_weight || '',
          intensity: 5,
        });
        setIsResting(true);
        setRestTime(180);
      } else {
        // Workout finished
        await handleFinishWorkout();
      }
    } catch (error) {
      console.error('Error saving series:', error);
      alert('Error al guardar la serie');
    }
  };

  const handleFinishWorkout = async () => {
    try {
      const endTime = new Date().toISOString();
      const totalSeconds = Math.floor((Date.now() - startTime) / 1000);
      
      await workoutService.finish(logId, {
        end_time: endTime,
        total_time_seconds: totalSeconds,
      });
      
      navigate(`/summary/${logId}`);
    } catch (error) {
      console.error('Error finishing workout:', error);
    }
  };

  const skipRest = () => {
    setIsResting(false);
    setRestTime(0);
  };

  if (!workout || !workout.routine) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-2xl">Cargando...</div>
        </div>
      </Layout>
    );
  }

  const currentExercise = workout.routine.routine_exercises[currentExerciseIndex];
  const exercise = currentExercise?.exercise;

  if (isResting) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-screen">
          <h1 className="text-4xl font-bold mb-8">Descansa</h1>
          <div className="text-9xl font-bold text-blue-600 mb-8">{restTime}s</div>
          <p className="text-xl mb-8">
            Siguiente: {currentSeries <= currentExercise.target_series 
              ? `Serie ${currentSeries} de ${exercise.name}` 
              : workout.routine.routine_exercises[currentExerciseIndex + 1]?.exercise.name}
          </p>
          <button
            onClick={skipRest}
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-lg text-xl flex items-center gap-2"
          >
            <SkipForward size={24} />
            Saltear Descanso
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{exercise.name}</h1>
              <p className="text-xl text-gray-600">{exercise.muscle_group?.name}</p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 mb-8">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600">Serie</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {currentSeries}/{currentExercise.target_series}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Meta Reps</p>
                  <p className="text-3xl font-bold text-blue-600">{currentExercise.target_reps}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Meta Peso</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {currentExercise.target_weight || 0} kg
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleFinishSeries}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-6 px-8 rounded-lg text-2xl shadow-lg transform hover:scale-105 transition-transform flex items-center justify-center gap-3"
            >
              <Check size={32} />
              FINALIZAR SERIE
            </button>

            <div className="mt-6">
              <p className="text-center text-gray-600">
                Ejercicio {currentExerciseIndex + 1} de {workout.routine.routine_exercises.length}
              </p>
              <button
                onClick={handleFinishWorkout}
                className="w-full mt-4 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded flex items-center justify-center gap-2"
              >
                <X size={20} />
                Terminar Entrenamiento
              </button>
            </div>
          </div>
        </div>

        {showSeriesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold mb-6">Registrar Serie</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Repeticiones Hechas</label>
                  <input
                    type="number"
                    required
                    min="0"
                    className="w-full px-3 py-2 border rounded-md text-lg"
                    value={seriesData.actual_reps}
                    onChange={(e) => setSeriesData({ ...seriesData, actual_reps: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Peso Utilizado (kg)</label>
                  <input
                    type="number"
                    required
                    step="0.5"
                    min="0"
                    className="w-full px-3 py-2 border rounded-md text-lg"
                    value={seriesData.actual_weight}
                    onChange={(e) =>
                      setSeriesData({ ...seriesData, actual_weight: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Intensidad RPE (1-9): {seriesData.intensity}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="9"
                    className="w-full"
                    value={seriesData.intensity}
                    onChange={(e) => setSeriesData({ ...seriesData, intensity: e.target.value })}
                  />
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Fácil</span>
                    <span>Medio</span>
                    <span>Máximo</span>
                  </div>
                </div>
                <button
                  onClick={handleSaveSeriesData}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg text-lg"
                >
                  CONFIRMAR Y DESCANSAR
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Workout;
